export type SelfGuardState =
  /** The signed-in admin is looking at their own account. Block destructive self-actions. */
  | "self"
  /** Looking at somebody else. Normal flow. */
  | "other"
  /**
   * Could not verify identity — either `SessionUser.uid` or the
   * target user's `firebaseUid` is missing. Fail-safe: block destructive
   * actions with an "unverified" banner instead of silently allowing
   * them. This state exists because `firebaseUid` is declared required
   * in the `UserListItem`/`UserDetail` contract (see API-1.5 / API-1.8),
   * but TypeScript types are compile-time only — a backend drift that
   * omits the field at runtime would otherwise defeat the self-guard
   * entirely. Intentionally conservative until API-1.14 lands and the
   * backend enforces `SELF_DEACTIVATION_FORBIDDEN` / the equivalent
   * for role changes as a hard fallback.
   */
  | "unverified";

/**
 * Classify the relationship between the signed-in admin and the target
 * user. Both inputs come from different identity namespaces handled
 * defensively:
 *  - `adminFirebaseUid` is the Firebase UID from `SessionUser.uid`.
 *    Expected present on an authenticated page, but tolerated as
 *    null/undefined so this helper can't throw.
 *  - `targetFirebaseUid` is the target user's Firebase UID from the
 *    API payload. Required by the contract, but verified at runtime.
 *
 * Also rejects empty strings because `""` compares equal to `""` and
 * would wrongly classify two unknown users as the same person.
 */
export function resolveSelfGuard(
  adminFirebaseUid: string | null | undefined,
  targetFirebaseUid: string | null | undefined,
): SelfGuardState {
  if (!adminFirebaseUid || !targetFirebaseUid) return "unverified";
  return adminFirebaseUid === targetFirebaseUid ? "self" : "other";
}

/** Convenience: should a destructive self-action be blocked? */
export function shouldBlockSelfAction(state: SelfGuardState): boolean {
  return state !== "other";
}
